import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setWorks } from "../../redux";

import { LangContext } from "../../context/LangContext";
import { useGoPage } from "../../hooks/useGoPage";
import { useToEmbed } from "../../hooks/useToEmbed";
import { useInsertBoldTag } from "../../hooks/useInsertBoldTag";

import SelectLang from "../../components/SelectLang";
import Slider from '../../components/Slider';
import Gallery from '../../components/Gallery';
import TooltipPortal from "../../components/TooltipPortal";
import Video from "../../components/Video";
import Modal from './Modal';
import ConfirmDelete from "../../components/ConfirmDelete";

import "./style.css"

import btn_amazon from '../../assets/img/amazon.png'
import award_icon from '../../assets/svg/laurel_wreath.svg';

function Work({ work, type }) {
    const goPage = useGoPage();
    const toEmbed = useToEmbed();
    const insertBoldTag = useInsertBoldTag();
    
    const awardRefs = useRef({});
    const dispatch = useDispatch();

    const worksDb = useSelector(state => state.works);
    const headerWidth = useSelector(state => state.options.headerWidth);
    const { language, translations } = useContext(LangContext);

    const [activeTooltip, setActiveTooltip] = useState(null);
    const [toDelete, setToDelete] = useState(false);
    const [inSubmit, setInSubmit] = useState(false);
    const [logoStyle, setLogoStyle] = useState({});
    const [formData, setFormData] = useState(null);
    const [modal, setModal] = useState(null);

    useEffect(() => {
        if (!work) {
            goPage('error');
            return
        }
        if (work !== 'new') {
            setFormData({
                ...work,
                video: work.video.length === 0 ? [''] : work.video
            });
            return
        }

        setFormData({
            mini: null,
            logo: null,
            title: '',

            descrip_fr: '',
            descrip_en: '',

            pg_fr: '',
            pg_en: '',

            editor: '',
            genre: '',

            theme_fr: '',
            theme_en: '',

            in_progress_fr: '',
            in_progress_en: '',

            patreon: '',
            chapiter: '',

            amazon: '',
            awards_fr: ['', ''],
            awards_en: ['', ''],

            video: [''],
            images: [],
            gallery: [],

            type: type,
            published: false,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, work]);

    useEffect(() => {
        if (!formData?.logo?.url) {
            setLogoStyle({})
            return;
        }

        const img = new Image();
        img.src = formData.logo.url;

        img.onload = () => {
            const heightMax = headerWidth < 700 ? 180 : 340;

            // height
            if (img.naturalWidth < img.naturalHeight) {
                setLogoStyle({
                    height: `${heightMax}px`,
                    aspectRatio: `${img.naturalWidth} / ${img.naturalHeight}`,
                    backgroundImage: `url(${formData.logo.url})`
                });
            }
            // width
            else {
                setLogoStyle({
                    width: '100%',
                    maxHeight: img.naturalHeight > heightMax ? `${heightMax}px` : 'unset',
                    aspectRatio: `${img.naturalWidth} / ${img.naturalHeight}`,
                    backgroundImage: `url(${formData.logo.url})`
                });
            }
        };
    }, [headerWidth, formData]);

    const return_in_back = () => {
        const opt_return = localStorage.getItem('opt_return_work_pg_vanrah');

        if (opt_return === 'vanrah') goPage('');
        else goPage(type === 1 ? 'mangas' : 'works');
    }

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return

        const url = URL.createObjectURL(file);

        setFormData({
            ...formData,
            logo: { url, file }
        });
    }

    const setValue = (e, field) => {
        setFormData({
            ...formData,
            [field]: e.target.value
        })
    }

    const deleteWork = async () => {
        if (inSubmit) return alert("Chargement en cours, veuillez patienter");
        else setInSubmit(true);

        const req = await fetch(`${process.env.REACT_APP_API_URL}/works/del/${formData._id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token_vanrah')}` }
        });
        const res = await req.json();
        setInSubmit(false);

        if (req.status !== 200) {
            alert(res.message);
            setToDelete(false);
            return
        }

        const newWorksList = worksDb.filter(i => i._id !== formData._id);
        dispatch(setWorks(newWorksList));

        alert("L'oeuvre a bien été supprimé !");
        return_in_back();
    }

    const submitForm = async () => {
        if (inSubmit) return alert("Chargement en cours, veuillez patienter");
        else setInSubmit(true);

        if (!formData.title || formData.title === '') {
            alert('Titre manquant');
            setInSubmit(false);
            return
        }

        const body = new FormData();

        body.append('title', formData.title);
        body.append('descrip_fr', formData.descrip_fr);
        body.append('descrip_en', formData.descrip_en);
        body.append('editor', formData.editor);
        body.append('pg_fr', formData.pg_fr);
        body.append('pg_en', formData.pg_en);
        body.append('genre', formData.genre);
        body.append('theme_fr', formData.theme_fr);
        body.append('theme_en', formData.theme_en);
        body.append('in_progress_fr', formData.in_progress_fr);
        body.append('in_progress_en', formData.in_progress_en);
        body.append('patreon', formData.patreon);
        body.append('chapiter', formData.chapiter);
        body.append('amazon', formData.amazon);

        const video = formData.video.filter(v => v !== '').map(v => toEmbed(v));
        body.append('video', JSON.stringify(video));

        // Ajoute les récompense en "fr"
        const awards_fr = [];
        for (let value of formData.awards_fr) {
            if (value !== '') awards_fr.push(value);
        }
        body.append('awards_fr', JSON.stringify(awards_fr));

        // Ajoute les récompense en "en"
        const awards_en = [];
        for (let value of formData.awards_en) {
            if (value !== '') awards_en.push(value);
        }
        body.append('awards_en', JSON.stringify(awards_en));

        body.append('type', type);
        body.append('published', formData.published);

        // Ajoute le fichier du logo
        if (!formData.logo || !formData.logo.url) {
            alert('Le logo est manquant');
            setInSubmit(false);
            return
        }
        if (formData.logo && formData.logo.file) {
            body.append('logo', formData.logo.file, formData.logo.file.name); // Ajoute le nom du fichier
        }

        // Gestion des images principales
        if (!formData.images || formData.images.length === 0) {
            alert('Au moin une image est nécéssaire');
            setInSubmit(false);
            return
        }
        if (formData.images && Array.isArray(formData.images)) {
            // Envoyer d'abord l'ordre des images
            body.append('images_order', JSON.stringify(formData.images.map(img => ({
                type: img.file ? 'new' : 'existing',
                url: img.url || null,
                _url: img._url || null
            }))));

            // Ensuite envoyer les images
            formData.images.forEach((img, index) => {
                if (img.url && !img.file) {
                    // Image existante
                    body.append(`existing_images[${index}][url]`, img.url);
                    body.append(`existing_images[${index}][_url]`, img._url);
                }
                else if (img.file) {
                    // Nouvelle image
                    body.append('images', img.file);
                }
            });
        }

        // Gestion des images de la gallerie
        if (formData.gallery && Array.isArray(formData.gallery)) {
            // Envoyer d'abord l'ordre des images de la gallerie
            body.append('gallery_order', JSON.stringify(formData.gallery.map(img => ({
                type: img.file ? 'new' : 'existing',
                url: img.url || null,
                _url: img._url || null
            }))));

            // Ensuite envoyer les images
            formData.gallery.forEach((img, index) => {
                if (img.url && !img.file) {
                    // Image existante
                    body.append(`existing_gallery[${index}][url]`, img.url);
                    body.append(`existing_gallery[${index}][_url]`, img._url);
                }
                else if (img.file) {
                    // Nouvelle image
                    body.append('gallery', img.file);
                }
            });
        }

        const req = await fetch(`${process.env.REACT_APP_API_URL}/works/${formData._id ? `edit/${formData._id}` : 'add'}`, {
            method: formData._id ? 'PUT' : 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token_vanrah')}` },
            body
        });
        const res = await req.json();
        setInSubmit(false);

        if (req.status !== 201 && req.status !== 200) {
            alert(res.message);
            return
        }

        if (formData._id) {
            const newWorkDb = worksDb.map(e => {
                if (e._id === formData._id) return res;
                return e;
            });
            dispatch(setWorks(newWorkDb));
            alert('Oeuvre modifié avec succès !');
        }
        else {
            dispatch(setWorks([...worksDb, res]));
            alert('Oeuvre ajouté avec succès !');
        }
        return_in_back();
    }

    return formData && (
        <div className='page page_work'>
            <button
                className="btn_return"
                onClick={() => return_in_back()}
            >
                {'<'}-<span>{translations.return}</span>--
            </button>

            {!formData._id &&
                <p className='title_work'>
                    - <input
                        type="text"
                        placeholder="Titre de l'oeuvre"

                        value={formData.title}
                        onChange={(e) => setValue(e, 'title')}
                    /> -
                </p>
            }

            <SelectLang />

            <div className="awards">
                <button onClick={() => setModal('awards')}>
                    <img
                        src={award_icon}
                        alt="Award(s)"

                        ref={el => awardRefs.current[0] = el}
                        onMouseEnter={() => setActiveTooltip(0)}
                        onMouseLeave={() => setActiveTooltip(null)}
                    />
                </button>

                {formData[`awards_${language}`] &&
                    <TooltipPortal
                        targetRef={{ current: awardRefs.current[0] }}
                        show={activeTooltip === 0}
                    >
                        {formData[`awards_${language}`].map((award, id) => (
                            <li key={id}>{award}</li>
                        ))}
                    </TooltipPortal>
                }
            </div>

            <section className="sect_slider_descrip">
                <div className="slider_amazon">
                    <Slider images={formData.images} setImages={setFormData} />

                    <button
                        className='btn_amazon'
                        onClick={() => setModal('amazon')}
                    ><img src={btn_amazon} alt="" /></button>
                </div>

                <div className="description">
                    <div className="logo_work">
                        <i style={logoStyle}></i>
                        {!formData.logo && <span>Ajouter un logo</span>}
                        <input type="file" onChange={(e) => handleFile(e)} />
                    </div>

                    <div className="fields">
                        <div className="textarea">
                            <span className="label">{translations.detail_and_descrip} :</span>

                            <div className="toolbar">
                                <button
                                    type="button"
                                    onClick={() => insertBoldTag('.page_work', formData, setFormData, language)}
                                >Gras</button>
                            </div>

                            <textarea
                                className="value"
                                value={formData[`descrip_${language}`]}
                                onChange={(e) => setValue(e, `descrip_${language}`)}
                                placeholder={`Description de la page en ${language === "fr" ? '"Français"' : '"Anglais"'}`}
                            ></textarea>
                        </div>

                        <button
                            className='btn_published'
                            onClick={() => setFormData({
                                ...formData,
                                published: !formData.published
                            })}
                        >publié<span>{formData.published && '✔'}</span></button>

                        <p className="field">
                            <span>{translations.editor} :</span>
                            <input
                                type="text"
                                value={formData.editor}
                                onChange={(e) => setValue(e, 'editor')}
                                placeholder="Éditeur"
                            />
                        </p>

                        <p className="field">
                            <span>{translations.pegi} :</span>
                            <input
                                type="text"
                                value={formData[`pg_${language}`]}
                                onChange={(e) => setValue(e, `pg_${language}`)}
                                placeholder="Age"
                            />
                        </p>

                        <p className="field">
                            <span>{translations.genre} :</span>
                            <input
                                type="text"
                                value={formData.genre}
                                onChange={(e) => setValue(e, 'genre')}
                                placeholder="Genre"
                            />
                        </p>

                        <p className="field">
                            <span>{translations.theme} :</span>
                            <input
                                type="text"
                                value={formData[`theme_${language}`]}
                                onChange={(e) => setValue(e, `theme_${language}`)}
                                placeholder={`Theme en ${language === 'fr' ? '"Français"' : '"Anglais"'}`}
                            />
                        </p>

                        <p className="field">
                            <span>{translations.ongoing_series} :</span>
                            <input
                                type="text"
                                value={formData[`in_progress_${language}`]}
                                onChange={(e) => setValue(e, `in_progress_${language}`)}
                                placeholder="Progression de la serie"
                            />
                        </p>

                        <p className="field">
                            <span className="blue">{translations.read_online} :</span>
                            <input
                                type="text"
                                value={formData.patreon}
                                onChange={(e) => setValue(e, 'patreon')}
                                placeholder="https://lire-en-ligne.fr"
                            />
                        </p>

                        <p className="field">
                            <span className="blue">{translations.read_first_chapter} :</span>
                            <input
                                type="text"
                                value={formData.chapiter}
                                onChange={(e) => setValue(e, 'chapiter')}
                                placeholder="https://mon-1er-chapitre.fr"
                            />
                        </p>
                    </div>
                </div>
            </section>

            <section className="sect_video">
                <p className="subtitle_page">
                    - <span>Trailer</span> -
                </p>

                {formData.video.map((v, idx) => <Video key={idx} data={formData} counter={idx} setData={setFormData} />)}
            </section>

            <section className="sect_gallery">
                <p className="subtitle_page">
                    - <span>{translations.gallery}</span> -
                </p>

                <Gallery gallery={formData.gallery} setGallery={setFormData} />
            </section>

            <Modal type={modal} isOpen={setModal} work={formData} setWork={setFormData} />

            <div className="panel_form">
                {formData._id &&
                    <button
                        className="btn_delete"
                        onClick={() => setToDelete(formData)}
                    >supprimer</button>
                }

                {formData._id &&
                    <button
                        className="btn_submit"
                        onClick={() => submitForm()}
                    >modifier</button>
                }

                {!formData._id &&
                    <button
                        className="btn_submit"
                        onClick={() => submitForm()}
                    >ajouter</button>
                }
            </div>

            {toDelete &&
                <ConfirmDelete
                    title="Supprimer une oeuvre"
                    itemName={toDelete.title}
                    setToDelete={setToDelete}
                    _delete={deleteWork}
                />
            }
        </div>
    )
}

export default Work;